import React, { useState, useEffect } from 'react';
import { Card, Spin, Empty, Tabs, Collapse, Image, Typography, Row, Col, Divider } from 'antd';
import { FileTextOutlined, PictureOutlined } from '@ant-design/icons';

const { Text, Paragraph } = Typography;
const { Panel } = Collapse;

const PptExtractViewer = ({ url }) => {
  const [loading, setLoading] = useState(true);
  const [pptData, setPptData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!url) {
      setLoading(false);
      return;
    }

    const fetchPptData = async () => {
      try {
        setLoading(true);
        const response = await fetch(url);
        const data = await response.json();
        
        if (data && data.PptExtraction) {
          setPptData(data.PptExtraction);
        } else {
          setError('无法解析PPT提取数据');
        }
      } catch (err) {
        console.error('获取PPT数据失败:', err);
        setError('获取PPT数据失败');
      } finally {
        setLoading(false);
      }
    };

    fetchPptData();
  }, [url]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '40px 0' }}>
        <Spin>
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <div>加载PPT数据中...</div>
          </div>
        </Spin>
      </div>
    );
  }

  if (error || !pptData) {
    return <Empty description={error || '无PPT数据'} />;
  }

  const { KeyFrameList = [] } = pptData;

  return (
    <div className="ppt-extract-viewer">
      <Tabs
        defaultActiveKey="frames"
        items={[
          {
            key: "frames",
            label: <span><PictureOutlined /> PPT页面 ({KeyFrameList.length})</span>,
            children: (
              <div style={{ overflowX: 'auto' }}>
                <Row gutter={[16, 16]} style={{ flexWrap: 'nowrap' }}>
                  {KeyFrameList.map((frame, index) => (
                    <Col key={index} style={{ minWidth: '250px' }}>
                      <Card
                        hoverable
                        size="small"
                        cover={
                          <div style={{ height: '160px', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Image
                              alt={`幻灯片 ${frame.Id}`}
                              src={frame.FileUrl}
                              style={{ maxHeight: '160px', objectFit: 'contain' }}
                              preview={{
                                mask: `幻灯片 ${frame.Id}`,
                              }}
                            />
                          </div>
                        }
                        title={`幻灯片 ${frame.Id}`}
                      >
                        <Text type="secondary">
                          {`${formatTime(frame.Start)} - ${formatTime(frame.End)}`}
                        </Text>
                        {frame.Summary && (
                          <Paragraph
                            ellipsis={{ rows: 2, expandable: true, symbol: '更多' }}
                            style={{ marginTop: '8px', fontSize: '12px' }}
                          >
                            {frame.Summary}
                          </Paragraph>
                        )}
                      </Card>
                    </Col>
                  ))}
                </Row>
              </div>
            ),
          },
          {
            key: "summary",
            label: <span><FileTextOutlined /> PPT内容摘要</span>,
            children: (
              <Collapse defaultActiveKey={KeyFrameList.filter(f => f.Summary).map((_, i) => i.toString())}>
                {KeyFrameList.filter(f => f.Summary).map((frame, index) => (
                  <Panel
                    header={
                      <div>
                        <span style={{ marginRight: '12px' }}>幻灯片 {frame.Id}</span>
                        <Text type="secondary">
                          {`${formatTime(frame.Start)} - ${formatTime(frame.End)}`}
                        </Text>
                      </div>
                    }
                    key={index.toString()}
                  >
                    <div>
                      <Paragraph style={{ whiteSpace: 'pre-line' }}>
                        {frame.Summary}
                      </Paragraph>
                      <Divider dashed />
                      <Image
                        width={200}
                        src={frame.FileUrl}
                        alt={`幻灯片 ${frame.Id}`}
                      />
                    </div>
                  </Panel>
                ))}
              </Collapse>
            ),
          },
        ]}
      />
    </div>
  );
};

// 格式化时间显示 (毫秒 -> mm:ss)
const formatTime = (ms) => {
  if (!ms && ms !== 0) return '--:--';
  
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

export default PptExtractViewer; 